import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Controller, FieldErrors, useForm} from 'react-hook-form';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {FormField} from '@components/system/FormField/FormField';
import FormDialog from '@components/system/FormDialog/FormDialog';
import {addPatientProblem} from '@shared/services/CCMServices';
import {Checkbox, Switch} from '@material-ui/core';

export interface IAddPatientProblemFields {
  patient_problem: string;
  icd10_code: string;
  is_chronic: boolean;
}

interface IAddPatientProblem {
  open: boolean;
  onClose: (res: 'confirm' | 'cancel') => void;
  clinicGroupId: number;
  clinicId: number;
  onSuccess?: (newProblem: any) => void;
}

const AddPatientProblem = (props: IAddPatientProblem): JSX.Element => {
  const {open, onClose, clinicGroupId, clinicId, onSuccess} = props;
  const handleClose = (res: 'confirm' | 'cancel' = 'cancel') => {
    onClose(res);
  };
  const {t} = useTranslation();
  const {
    control,
    handleSubmit,
    errors,
    watch,
    formState: {isSubmitting}
  } = useForm<IAddPatientProblemFields>({
    mode: 'all',
    resolver: values => {
      const validationErrors: FieldErrors<IAddPatientProblemFields> = {};
      if (!values.patient_problem || !values.patient_problem.trim()) {
        validationErrors.patient_problem = {
          type: 'required',
          message: t('CCM.ADD_PATIENT_PROBLEM.PROBLEM_IS_REQUIRED')
        };
      }
      if (!values.icd10_code || !values.icd10_code.trim()) {
        validationErrors.icd10_code = {
          type: 'required',
          message: t('CCM.ADD_PATIENT_PROBLEM.ICD10_CODE_IS_REQUIRED')
        };
      }
      if (Object.keys(validationErrors).length) {
        return {
          values: {},
          errors: validationErrors
        };
      }
      return {
        values,
        errors: {}
      };
    }
  });
  const {enqueueSnackbar} = useSnackbar();
  const addPatientProblemFeilds = [
    {
      label: t('CCM.ADD_PATIENT_PROBLEM.PROBLEM_LABEL'),
      name: 'patient_problem',
      type: 'text',
      placeholder: t('CCM.ADD_PATIENT_PROBLEM.PROBLEM_PLACEHOLDER')
    },
    {
      label: t('CCM.ADD_PATIENT_PROBLEM.ICD10_CODE_LABEL'),
      name: 'icd10_code',
      type: 'text',
      placeholder: t('CCM.ADD_PATIENT_PROBLEM.ICD10_CODE_PLACEHOLDER')
    }
  ];
  const checkToAddPatientProblemToSelectedProblems = watch(
    'enable-to-add-patient-problem'
  );
  const handleSuccess = (newProblem: any) => {
    if (onSuccess) {
      onSuccess(newProblem);
    }
    handleClose('confirm');
  };
  const handleAddMPatientProblem = async (data: IAddPatientProblemFields) => {
    try {
      const params: {clinic_id?: number} = {};
      if (clinicId) {
        params.clinic_id = clinicId;
      }
      const res = await addPatientProblem({
        clinic_group_id: clinicGroupId,
        payload: {
          patient_problem: data?.patient_problem,
          icd10_code: data?.icd10_code,
          is_chronic: data?.is_chronic
        },
        params
      });
      if (checkToAddPatientProblemToSelectedProblems ?? true) {
        handleSuccess({
          problem: data?.patient_problem,
          icd10_code: data?.icd10_code,
          is_chronic: data?.is_chronic,
          id: res?.data?.patient_problem_id,
          actions: null
        });
      }
      enqueueSnackbar(res.message, SnackBarConfig('success'));
      handleClose('confirm');
    } catch (e) {
      if (e?.message) {
        enqueueSnackbar(e.message, SnackBarConfig('error'));
      }
    }
  };
  return (
    <FormDialog
      isSubmitting={isSubmitting}
      title={
        <Trans
          i18nKey={'CCM.ADD_PATIENT_PROBLEM.TITLE'}
          components={[<strong key={'X'} />]}
        />
      }
      description={''}
      subTitle={''}
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit(handleAddMPatientProblem)}>
      <div style={{paddingLeft: '9px', paddingRight: '9px'}}>
        <div
          className="sidesMainView"
          style={{display: 'flex', gap: '1rem', flexDirection: 'column'}}>
          {addPatientProblemFeilds.map((field, index) => (
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              render={fieldProps => (
                <div style={{display: 'grid', position: 'relative'}}>
                  <FormField
                    {...field}
                    error={errors[field.name]}
                    type={field.type}
                    {...fieldProps}
                  />
                </div>
              )}
            />
          ))}
        </div>
        <div style={{marginTop: '0.5rem'}}>
          {t('CCM.ADD_PATIENT_PROBLEM.IS_CRONIC_LABEL')}
          <span style={{marginLeft: '1rem'}}>{t('ADD_CLINIC.NO')}</span>
          <Controller
            name="is_chronic"
            control={control}
            defaultValue={true}
            render={({onBlur, onChange, value}) => (
              <Switch
                color="primary"
                style={{marginBottom: '0.5rem'}}
                inputProps={{'aria-label': 'primary checkbox'}}
                onBlur={onBlur}
                checked={value}
                onChange={(event, checked) => {
                  onChange(checked);
                }}
              />
            )}
          />
          {t('ADD_CLINIC.YES')}
        </div>
      </div>
      <div>
        <Controller
          id={'enable-to-add-patient-problem'}
          defaultValue={true}
          render={({...inputProps}) => (
            <Checkbox
              data-test-id="pcl-practices-sendNotificationCheckbox"
              {...inputProps}
              checked={inputProps.value}
              onChange={(event, checked) => inputProps.onChange(checked)}
            />
          )}
          control={control}
          name={'enable-to-add-patient-problem'}
        />
        {t('CCM.ADD_PATIENT_PROBLEM.ADD_TO_SELECTED_PROBLEMS')}
      </div>
    </FormDialog>
  );
};

export default AddPatientProblem;
